import type { ReactNode } from 'react';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  type BadgeProps,
  Flex,
  theme,
  Tooltip,
  type TooltipProps,
} from 'antd';
import { createTooltipWarningProps } from 'pos-client/utils/tooltipUtils.tsx';

import PosText, { type PosTextProps } from './PosText.tsx';

export type PosDisabledWarningType = {
  /** Unique identifier for the warning */
  key: string;
  /** Condition of when warning should eval to true */
  condition: boolean;
  /** Disabled warning message */
  text: string;
};

/**
 * Props for {@link PosDisabledWarning}
 */
interface PosDisabledWarningProps {
  /** Single or array of {@link PosDisabledWarning} items*/
  warnings: PosDisabledWarningType | PosDisabledWarningType[];
  /** Tooltip props for displaying warning messages _(Hidden when undefined)_ @see {@link TooltipProps}*/
  tooltipProps?: Omit<TooltipProps, 'title' | 'open' | 'defaultOpen' | 'fresh'>;
  /** Badge props for displaying warning message count @see {@link BadgeProps}*/
  badgeProps?: Omit<BadgeProps, 'count'>;
  /** Show warning message under component when count is 1 @defaultValue false */
  showWarningOnSingular?: boolean; // Shows warning message under element instead of in tooltip
  /** Text props for warning message under component @remarks `showWarningOnSingular` must be true */
  warningSingularTextProps?: Omit<PosTextProps, 'icon' | 'colProps' | 'column'>;
  /** Loading state of badge count */
  loading?: boolean;
  children?: ReactNode;
}

/**
 * @component
 * @description Display badge count and tooltip for reasons why a button has been disabled
 *
 * @see {@link https://ant.design/components/badge | AntD Badge Component}
 * @param props {@link PosDisabledWarningProps}
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * const [orderStatusValid, setOrderStatusValid] = useState(false);
 *
 * const completeOrderDisabledWarnings: PosDisabledWarningType[] = [
 *   {
 *     key: 'orderStatusValid',
 *     conditions: !orderStatusValid,
 *     text: t('orders.disabledErrors.allJobsCompletedOrCancelledError')
 *   }
 * ];
 *
 * const completeOrderDisabled = completeOrderDisabledWarnings.some(({condition}) => condition);
 *
 * <PosDisabledWarning warnings={completeOrderDisabledWarnings}>
 *   <Button
 *     type="primary"
 *     icon={<FontAwesomeIcon icon={faCalendarClock} />}
 *     onClick={completeOrder}
 *     disabled={completeOrderDisabled}
 *   >
 *     {t('orders.completeOrder')}
 *   </Button>
 * </PosDisabledWarning>
 *
 * ```
 */
export const PosDisabledWarning = ({
  warnings,
  showWarningOnSingular,
  warningSingularTextProps,
  tooltipProps,
  badgeProps,
  loading,
  children,
}: PosDisabledWarningProps) => {
  const { token } = theme.useToken();

  const disabledWarnings = Array.isArray(warnings) ? warnings : [warnings];
  const activeWarnings = disabledWarnings.filter(({ condition }) => condition);
  const activeWarningCount = activeWarnings.length;

  const badgeWarningProps: BadgeProps = {
    count: (
      <FontAwesomeIcon
        color={token.colorWarningText}
        icon={faExclamationCircle}
      />
    ),
    color: token.colorWarningText,
    ...badgeProps,
  };

  const tooltipWarningProps = createTooltipWarningProps({
    activeWarningCount,
    activeWarnings,
    token,
    tooltipProps,
  });

  if (showWarningOnSingular && activeWarningCount === 1)
    return (
      <Flex
        align="top"
        vertical
        style={{
          width: 'min-content',
        }}
      >
        {children}
        <PosText
          icon={faExclamationTriangle}
          color={token.colorWarningText}
          styles={{
            container: {
              verticalAlign: 'top',
              textAlign: 'start',
              alignItems: 'baseline',
            },
          }}
          {...warningSingularTextProps}
        >
          {activeWarnings[0].text}
        </PosText>
      </Flex>
    );

  if ((activeWarningCount === 0 && !badgeProps?.showZero) || loading) {
    return children;
  }

  return (
    <Tooltip {...tooltipWarningProps}>
      <Badge {...badgeWarningProps}>{children}</Badge>
    </Tooltip>
  );
};
